import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { LogOut } from "lucide-react";

import { cn } from "@/lib/utils";

type Props = {
  name: string | null | undefined;
  email: string | null | undefined;
  image: string | null | undefined;
  /** Async handler that calls ``POST /v1/auth/logout`` on the gateway
   *  (which clears the ``kos_session`` cookie) and bounces the user
   *  back to /login. Provided by the dashboard shell. */
  signOutAction: () => Promise<void>;
};

/**
 * Header avatar + dropdown. Lives in `AppHeader` so it stays
 * accessible regardless of whether the sidebar is collapsed. The
 * sign-out path is a plain async function (the SPA owns the cookie
 * clear via the gateway directly).
 */
export function HeaderUserMenu({ name, email, image, signOutAction }: Props) {
  const initial = (name || email || "?").trim().charAt(0).toUpperCase();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="User menu"
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-border/70 bg-card text-[11px] font-medium text-foreground shadow-sm",
            "transition hover:bg-muted",
          )}
        >
          {image ? (
            <img
              src={image}
              alt={name || email || "User"}
              width={28}
              height={28}
              className="rounded-full"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center bg-muted text-foreground">
              {initial}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className={cn(
            "z-50 min-w-[220px] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        >
          <div className="px-3 py-2">
            {name ? (
              <div className="truncate text-[13px] font-medium leading-tight">{name}</div>
            ) : null}
            {email ? (
              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{email}</div>
            ) : null}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            asChild
            className="cursor-pointer rounded-sm px-2 py-1.5 text-[13px] outline-none focus:bg-accent focus:text-accent-foreground"
          >
            <button
              type="button"
              onClick={() => {
                void signOutAction();
              }}
              className="flex w-full items-center gap-2 text-left"
            >
              <LogOut className="h-3.5 w-3.5 text-muted-foreground" />
              Sign out
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
